// src/components/security/CSRFProvider.tsx
import React, { createContext, useContext, useEffect, useState } from 'react';
import secureCookieAuthService from '../../services/secureCookieAuthService';

interface CSRFContextType {
  csrfReady: boolean;
  refreshCSRF: () => Promise<void>;
}

const CSRFContext = createContext<CSRFContextType | undefined>(undefined);

export const useCSRF = () => {
  const context = useContext(CSRFContext);
  if (context === undefined) {
    throw new Error('useCSRF must be used within a CSRFProvider');
  }
  return context;
};

interface CSRFProviderProps {
  children: React.ReactNode;
}

export const CSRFProvider: React.FC<CSRFProviderProps> = ({ children }) => {
  const [csrfReady, setCSRFReady] = useState(false);

  const initializeCSRF = async () => {
    try {
      await secureCookieAuthService.initCSRF();
      setCSRFReady(true);
    } catch (error) {
      console.error('Failed to initialize CSRF protection:', error);
      // Still set ready to true to allow app to function
      setCSRFReady(true);
    }
  };

  const refreshCSRF = async () => {
    setCSRFReady(false);
    await initializeCSRF();
  };

  useEffect(() => {
    initializeCSRF();
  }, []);

  return (
    <CSRFContext.Provider value={{ csrfReady, refreshCSRF }}>
      {children}
    </CSRFContext.Provider>
  );
};

export default CSRFProvider;